/* eslint-disable @typescript-eslint/naming-convention */
import type { AppFooterContent } from '@ps/web-martech-cms-types';
import { pick } from 'lodash';

import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type {
  ExtractDispatchFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import { pageChange } from '@swf/core/utils/actions';

type AppFooterContentState = Pick<
  AppFooterContent,
  | 'layout'
  | 'heading'
  | 'background_color'
  | 'background_fit'
  | 'background_image'
  | 'item_alignment'
  | 'items'
  | 'number_of_columns'
>;

const initialState: AppFooterContentState = {
  layout: {},
  heading: { hero: false },
  background_fit: undefined,
  background_image: {
    logged_in_image: {}
  },
  item_alignment: 'left',
  items: [],
  number_of_columns: 1
};

const reducers = {
  set: (state: AppFooterContentState, payload: AppFooterContentState) => payload
};

const effects = {
  async preload(
    payload: undefined,
    { actions, dispatch, widget: { uid } }: WidgetEffectParams<AppFooterContent>
  ) {
    const { fetch }: ExtractDispatchFromModel<AppFooterContentWidgetModel> = dispatch[uid];
    pageChange(actions)?.subscribe(() => fetch());

    fetch();
  },
  async fetch(
    payload: undefined,
    { request, dispatch, widget: { uid }, select, rootState }: WidgetEffectParams<AppFooterContent>
  ) {
    const { set }: ExtractDispatchFromModel<AppFooterContentWidgetModel> = dispatch[uid];
    const page_url = select.location.stripPrefix(rootState, undefined);

    try {
      const { body: data } = await request<AppFooterContentState>({
        service: '/api/v1-preview/cms/app/footer',
        body: { page_url }
      });

      set(
        data
          ? pick(data, [
              'layout',
              'heading',
              'background_color',
              'background_fit',
              'background_image',
              'item_alignment',
              'items',
              'number_of_columns'
            ])
          : initialState
      );
    } catch (error) {
      set(initialState);
    }
  }
};

const selectors = (slice: Slicer<AppFooterContentState>) => ({
  get() {
    return slice;
  }
});

export interface AppFooterContentWidgetModel
  extends WidgetModelConfig<AppFooterContent, AppFooterContentState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const appFooterContentWidgetModel: AppFooterContentWidgetModel = {
  state: initialState,
  reducers,
  effects,
  selectors
};

export const useAppFooterContentWidget: UseWidget<AppFooterContent, AppFooterContentWidgetModel> =
  useWidget;
